import { FC, Fragment } from "react"
import { Dialog, Transition } from "@headlessui/react"
import { X, Loader2 } from "lucide-react"
import { useFindOBLOsQuery } from "../../../../redux/queries/pricing-engine"

interface Props {
  open: boolean
  onClose: () => void
  brandId: string
  bizChannel: string
  originatorId: string
}

const OBProfileModal: FC<Props> = ({
  open,
  onClose,
  brandId,
  bizChannel,
  originatorId
}) => {
  const { data, isLoading, error } = useFindOBLOsQuery(
    {
      brand_id: brandId,
      biz_channel: bizChannel,
      originator_id: originatorId
    },
    { skip: !open }
  )

  const profile = data?.[0]

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-md rounded-lg bg-white shadow-xl">
                <div className="flex items-center justify-between border-b px-5 py-4">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">
                    OB Originator Profile
                  </Dialog.Title>
                  <button
                    type="button"
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <X size={20} />
                  </button>
                </div>

                <div className="px-5 py-4">
                  {isLoading && (
                    <div className="flex items-center justify-center py-8">
                      <Loader2
                        size={24}
                        className="animate-spin text-gray-400"
                      />
                    </div>
                  )}

                  {error && (
                    <div className="text-red-600 text-sm py-4">
                      Failed to load OB profile data.
                    </div>
                  )}

                  {!isLoading && !error && !profile && (
                    <div className="text-gray-500 text-sm py-4">
                      No originator found for this biz channel / originator ID.
                    </div>
                  )}

                  {profile && (
                    <dl className="divide-y divide-gray-100">
                      <Row
                        label="Name"
                        value={`${profile.firstName} ${profile.lastName}`}
                      />
                      <Row label="Username" value={profile.userName} />
                      <Row label="Email" value={profile.email} />
                      <Row label="Phone" value={profile.phoneNumber} />
                      <Row
                        label="Originator ID"
                        value={String(profile.index)}
                      />
                      <Row label="Access Level" value={profile.accessLevel} />
                      <Row
                        label="Biz Channel"
                        value={profile.biz_channel.name}
                      />
                      <Row
                        label="Biz Channel ID"
                        value={String(profile.biz_channel.id)}
                      />
                    </dl>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}

const Row: FC<{ label: string; value?: string }> = ({ label, value }) => (
  <div className="flex justify-between py-2.5 text-sm">
    <dt className="text-gray-500">{label}</dt>
    <dd className="font-medium text-gray-900">{value || "-"}</dd>
  </div>
)

export default OBProfileModal
